import React, { useState, useEffect, useCallback } from "react";
import { Phone, Mail, MapPin, Trash2, RefreshCw } from "lucide-react";
import SEO from "../components/SEO";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = "pereira_admin_token";

function readToken() {
  try { return sessionStorage.getItem(TOKEN_KEY) || ""; } catch (e) { return ""; }
}
function storeToken(t) {
  try { if (t) sessionStorage.setItem(TOKEN_KEY, t); else sessionStorage.removeItem(TOKEN_KEY); } catch (e) { /* stockage indisponible */ }
}

const STATUS_LABELS = {
  nouveau: { label: "Nouveau", color: "bg-blue-100 text-blue-800" },
  contacte: { label: "Contacté", color: "bg-yellow-100 text-yellow-800" },
  en_cours: { label: "En cours", color: "bg-purple-100 text-purple-800" },
  termine: { label: "Terminé", color: "bg-green-100 text-green-800" },
  archive: { label: "Archivé", color: "bg-slate-100 text-slate-600" },
};

export default function Admin() {
  const [leads, setLeads] = useState([]);
  const [counts, setCounts] = useState({});
  const [filter, setFilter] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(readToken);
  const [tokenInput, setTokenInput] = useState("");
  const [authError, setAuthError] = useState("");

  // Toute requete d'administration porte le jeton ; un refus (401/403) renvoie au formulaire.
  const adminFetch = useCallback(async (url, options = {}) => {
    const res = await fetch(url, {
      ...options,
      headers: { ...(options.headers || {}), Authorization: `Bearer ${token}` },
    });
    if (res.status === 401 || res.status === 403) {
      storeToken("");
      setToken("");
      setAuthError("Accès refusé : jeton invalide ou administration désactivée.");
      throw new Error("auth");
    }
    return res;
  }, [token]);

  const fetchLeads = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: page.toString(), limit: "15" });
      if (filter) params.set("status", filter);
      const res = await adminFetch(`${API_URL}/api/admin/leads?${params}`);
      const data = await res.json();
      setLeads(data.leads || []);
      setTotalPages(data.pages || 1);
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  }, [page, filter, adminFetch]);

  const fetchCounts = useCallback(async () => {
    try {
      const res = await adminFetch(`${API_URL}/api/admin/leads/count`);
      const data = await res.json();
      setCounts(data);
    } catch (e) {
      console.error(e);
    }
  }, [adminFetch]);

  useEffect(() => {
    if (!token) return;
    fetchLeads();
    fetchCounts();
  }, [token, fetchLeads, fetchCounts]);

  const updateStatus = async (leadId, newStatus) => {
    try {
      await adminFetch(`${API_URL}/api/admin/leads/${leadId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: newStatus }),
      });
    } catch (e) {
      return;
    }
    fetchLeads();
    fetchCounts();
  };

  const deleteLead = async (leadId) => {
    if (!window.confirm("Supprimer cette demande ?")) return;
    try {
      await adminFetch(`${API_URL}/api/admin/leads/${leadId}`, { method: "DELETE" });
    } catch (e) {
      return;
    }
    fetchLeads();
    fetchCounts();
  };

  if (!token) {
    return (
      <>
        <SEO title="Administration | Pereira Plomberie Chauffage" description="" />
        <section className="py-16">
          <form
            className="max-w-sm mx-auto px-4 space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              const t = tokenInput.trim();
              if (!t) return;
              storeToken(t);
              setAuthError("");
              setTokenInput("");
              setToken(t);
            }}
            data-testid="admin-login"
          >
            <h1 className="font-heading text-2xl font-bold uppercase tracking-tight">Administration</h1>
            <label className="block text-sm text-slate-600" htmlFor="admin-token">Jeton d'administration</label>
            <input
              id="admin-token"
              type="password"
              autoComplete="off"
              value={tokenInput}
              onChange={(e) => setTokenInput(e.target.value)}
              className="w-full border border-slate-300 rounded-sm px-3 py-2"
            />
            {authError && <p className="text-sm text-red-600" data-testid="admin-auth-error">{authError}</p>}
            <button type="submit" className="bg-secondary text-white px-4 py-2 rounded-sm text-sm font-medium">Accéder</button>
          </form>
        </section>
      </>
    );
  }

  return (
    <>
      <SEO title="Administration | Pereira Plomberie Chauffage" description="" />

      <section className="bg-primary text-white py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="font-heading text-2xl md:text-3xl font-bold uppercase tracking-tight" data-testid="admin-title">
            Tableau de bord
          </h1>
        </div>
      </section>

      <section className="py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
            {[
              { label: "Total", value: counts.total || 0, key: "" },
              { label: "Nouveau", value: counts.nouveau || 0, key: "nouveau" },
              { label: "Contacté", value: counts.contacte || 0, key: "contacte" },
              { label: "En cours", value: counts.en_cours || 0, key: "en_cours" },
              { label: "Terminé", value: counts.termine || 0, key: "termine" },
            ].map((stat) => (
              <button
                key={stat.label}
                onClick={() => { setFilter(stat.key); setPage(1); }}
                className={`p-4 rounded-sm border text-left transition-all ${
                  filter === stat.key ? "border-secondary bg-secondary/5" : "border-slate-200 bg-white hover:border-secondary/50"
                }`}
                data-testid={`stat-${stat.key || "total"}`}
              >
                <p className="text-2xl font-heading font-bold">{stat.value}</p>
                <p className="text-xs text-slate-500 uppercase tracking-wider">{stat.label}</p>
              </button>
            ))}
          </div>

          {/* Actions */}
          <div className="flex items-center gap-3 mb-4">
            <button onClick={() => { fetchLeads(); fetchCounts(); }} className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-secondary" data-testid="refresh-button">
              <RefreshCw size={14} /> Actualiser
            </button>
          </div>

          {/* Leads table */}
          <div className="bg-white rounded-sm border border-slate-200 overflow-x-auto">
            {loading ? (
              <div className="p-8 text-center text-slate-400">Chargement...</div>
            ) : leads.length === 0 ? (
              <div className="p-8 text-center text-slate-400" data-testid="no-leads">Aucune demande trouvée.</div>
            ) : (
              <table className="w-full text-sm" data-testid="leads-table">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="text-left p-3 font-medium uppercase tracking-wider text-xs">Nom</th>
                    <th className="text-left p-3 font-medium uppercase tracking-wider text-xs">Contact</th>
                    <th className="text-left p-3 font-medium uppercase tracking-wider text-xs hidden md:table-cell">Ville</th>
                    <th className="text-left p-3 font-medium uppercase tracking-wider text-xs hidden lg:table-cell">Type</th>
                    <th className="text-left p-3 font-medium uppercase tracking-wider text-xs">Statut</th>
                    <th className="text-left p-3 font-medium uppercase tracking-wider text-xs hidden lg:table-cell">Date</th>
                    <th className="text-left p-3 font-medium uppercase tracking-wider text-xs">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {leads.map((lead, i) => (
                    <tr key={i} className="hover:bg-slate-50" data-testid={`lead-row-${i}`}>
                      <td className="p-3 font-medium">{lead.nom}</td>
                      <td className="p-3">
                        <a href={`tel:${lead.telephone}`} className="flex items-center gap-1 text-secondary hover:underline">
                          <Phone size={12} /> {lead.telephone}
                        </a>
                        <a href={`mailto:${lead.email}`} className="flex items-center gap-1 text-slate-500 text-xs mt-1">
                          <Mail size={10} /> {lead.email}
                        </a>
                      </td>
                      <td className="p-3 hidden md:table-cell">
                        <span className="flex items-center gap-1 text-slate-600"><MapPin size={12} /> {lead.ville}</span>
                      </td>
                      <td className="p-3 hidden lg:table-cell text-slate-600">{lead.type_demande}</td>
                      <td className="p-3">
                        <select
                          value={lead.status}
                          onChange={(e) => updateStatus(lead.lead_id, e.target.value)}
                          className={`text-xs font-medium px-2 py-1 rounded-sm border-0 ${STATUS_LABELS[lead.status]?.color || ""}`}
                          data-testid={`status-select-${i}`}
                        >
                          {Object.entries(STATUS_LABELS).map(([key, val]) => (
                            <option key={key} value={key}>{val.label}</option>
                          ))}
                        </select>
                      </td>
                      <td className="p-3 hidden lg:table-cell text-xs text-slate-400">
                        {lead.created_at ? new Date(lead.created_at).toLocaleDateString("fr-FR") : "—"}
                      </td>
                      <td className="p-3">
                        <button onClick={() => deleteLead(lead.lead_id)} className="text-red-400 hover:text-red-600 p-1" data-testid={`delete-lead-${i}`}>
                          <Trash2 size={14} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-6">
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                <button
                  key={p}
                  onClick={() => setPage(p)}
                  className={`w-8 h-8 rounded-sm text-xs font-medium ${
                    page === p ? "bg-secondary text-white" : "bg-white border border-slate-200 hover:border-secondary"
                  }`}
                >
                  {p}
                </button>
              ))}
            </div>
          )}
        </div>
      </section>
    </>
  );
}
